import { useCallback, useEffect, useRef, useState } from 'react';
import * as notificationsApi from '@/services/notifications';
import type { AppNotification, CreateNotificationInput } from '@/services/notifications';

export type { AppNotification, NotificationType, NotificationEntityType } from '@/services/notifications';

/**
 * Supabase-backed notification store.
 * No in-memory seeding, no local IDs — every notification is persisted.
 */
export function useNotificationStore(currentUserId: string) {
  const signedIn = !!currentUserId && currentUserId !== '__none__';

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(0);

  const load = useCallback(async () => {
    if (!signedIn) {
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [{ items, hasMore: more }, count] = await Promise.all([
        notificationsApi.fetchNotificationsPage(currentUserId, 0),
        notificationsApi.fetchUnreadCount(currentUserId),
      ]);
      pageRef.current = 0;
      setNotifications(items);
      setHasMore(more);
      setUnreadCount(count);
    } catch (err) {
      console.error('[notifications] load failed', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [signedIn, currentUserId]);

  useEffect(() => { void load(); }, [load]);

  // ------------------------------------------------------------- realtime
  useEffect(() => {
    if (!signedIn) return;
    const unsubscribe = notificationsApi.subscribeToNotifications(currentUserId, {
      onInsert: (n) => {
        setNotifications((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
        if (!n.read) setUnreadCount((c) => c + 1);
      },
      onUpdate: (n) => {
        setNotifications((prev) => {
          const previous = prev.find((x) => x.id === n.id);
          if (previous && !previous.read && n.read) setUnreadCount((c) => Math.max(0, c - 1));
          return prev.map((x) => (x.id === n.id ? n : x));
        });
      },
      onDelete: (id) => {
        setNotifications((prev) => {
          const removed = prev.find((x) => x.id === id);
          if (removed && !removed.read) setUnreadCount((c) => Math.max(0, c - 1));
          return prev.filter((x) => x.id !== id);
        });
      },
    });
    return unsubscribe;
  }, [signedIn, currentUserId]);

  const loadMore = useCallback(async () => {
    if (!signedIn || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const { items, hasMore: more } = await notificationsApi.fetchNotificationsPage(currentUserId, next);
      pageRef.current = next;
      setNotifications((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...items.filter((n) => !seen.has(n.id))];
      });
      setHasMore(more);
    } catch (err) {
      console.error('[notifications] loadMore failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [signedIn, currentUserId, loadingMore, hasMore]);

  const addNotification = useCallback(
    async (data: Omit<CreateNotificationInput, 'actorId'> & { actorId?: string }) => {
      const actorId = data.actorId ?? currentUserId;
      if (!signedIn || !data.userId) return;
      if (data.userId === actorId) return; // never notify yourself
      try {
        await notificationsApi.insertNotification({ ...data, actorId });
      } catch (err) {
        console.error('[notifications] addNotification failed', err);
      }
    },
    [signedIn, currentUserId],
  );

  /** Notifications of the signed-in user (other users' feeds are not readable). */
  const getNotifications = useCallback(
    (userId: string) => (userId === currentUserId ? notifications : []),
    [notifications, currentUserId],
  );

  const getUnreadCount = useCallback(
    (userId: string) => (userId === currentUserId ? unreadCount : 0),
    [unreadCount, currentUserId],
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== notificationId) return n;
        wasUnread = !n.read;
        return { ...n, read: true };
      }),
    );
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await notificationsApi.markNotificationRead(notificationId);
    } catch (err) {
      console.error('[notifications] markAsRead failed', err);
    }
  }, []);

  const markAllAsRead = useCallback(async (userId: string) => {
    if (userId !== currentUserId) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationsApi.markAllNotificationsRead(userId);
    } catch (err) {
      console.error('[notifications] markAllAsRead failed', err);
      void load();
    }
  }, [currentUserId, load]);

  const removeNotification = useCallback(async (notificationId: string) => {
    let wasUnread = false;
    setNotifications((prev) =>
      prev.filter((n) => {
        if (n.id !== notificationId) return true;
        wasUnread = !n.read;
        return false;
      }),
    );
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await notificationsApi.deleteNotification(notificationId);
    } catch (err) {
      console.error('[notifications] delete failed', err);
      void load();
    }
  }, [load]);

  return {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    refresh: load,
    loadMore,
    addNotification,
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
  };
}
